<template>
  <div class="card training-summary-card">
    <div class="card-content">
      <div class="core-section-header">
        <div class="core-title-row">
          <i class="material-icons">school</i>

          <div>
            <span class="card-title core-card-title"> Staff Training Progress </span>

            <p class="core-card-subtitle">
              Training completion and compliance for active staff at this venue.
            </p>
          </div>
        </div>

        <button
          type="button"
          class="btn-flat"
          :disabled="loading || !cleanVenueId"
          @click="loadTrainingRecords"
        >
          <i class="material-icons left">refresh</i>
          Refresh
        </button>
      </div>

      <div class="core-divider"></div>

      <div v-if="loading" class="progress">
        <div class="indeterminate"></div>
      </div>

      <div
        v-else-if="!cleanVenueId"
        class="card-panel grey lighten-4 grey-text text-darken-2"
      >
        No venue selected.
      </div>

      <div v-else-if="error" class="card-panel red lighten-5 red-text text-darken-3">
        {{ error }}
      </div>

      <div
        v-else-if="!activeStaff.length"
        class="card-panel grey lighten-4 grey-text text-darken-2"
      >
        There are no active staff members at this venue.
      </div>

      <template v-else>
        <div class="overall-summary">
          <div
            class="overall-percentage core-percent"
            :class="percentageClass(overallPercentage)"
          >
            {{ overallPercentage }}%
          </div>

          <div class="overall-details">
            <strong>Overall compliance</strong>

            <span>
              {{ currentCount }} of {{ totalRequirements }}
              required training records are current.
            </span>
          </div>
        </div>

        <div
          class="core-progress core-progress--large core-progress--teal overall-progress-track"
        >
          <div
            class="core-progress-fill"
            :style="{ width: `${overallPercentage}%` }"
          ></div>
        </div>

        <div class="core-stat-grid core-stat-grid--3 summary-kpis">
          <div
            class="core-stat-tile core-stat-tile--inline core-stat-tile--success summary-kpi"
          >
            <span>Current</span>
            <strong>{{ currentCount }}</strong>
          </div>

          <div
            class="core-stat-tile core-stat-tile--inline core-stat-tile--warning summary-kpi"
          >
            <span>Due</span>
            <strong>{{ dueCount }}</strong>
          </div>

          <div
            class="core-stat-tile core-stat-tile--inline core-stat-tile--danger summary-kpi"
          >
            <span>Action required</span>
            <strong>{{ actionCount }}</strong>
          </div>
        </div>

        <div class="core-grid core-grid--3 course-summary-grid">
          <div v-for="course in courseSummaries" :key="course.id" class="core-panel">
            <div class="course-summary-heading">
              <div>
                <strong>{{ course.shortTitle }}</strong>

                <span> {{ course.current }} of {{ course.total }} current </span>
              </div>

              <strong class="core-percent" :class="percentageClass(course.percentage)">
                {{ course.percentage }}%
              </strong>
            </div>

            <div class="core-progress core-progress--teal course-progress-track">
              <div
                class="core-progress-fill"
                :style="{ width: `${course.percentage}%` }"
              ></div>
            </div>

            <div class="course-status-counts">
              <span class="green-text text-darken-3"> {{ course.current }} current </span>

              <span class="orange-text text-darken-3"> {{ course.due }} due </span>

              <span class="red-text text-darken-3">
                {{ course.actionRequired }} action
              </span>
            </div>
          </div>
        </div>

        <div class="table-wrap training-table-scroll">
          <table class="striped core-table training-table">
            <thead>
              <tr>
                <th>Staff member</th>

                <th v-for="course in courses" :key="course.id">
                  {{ course.shortTitle }}
                </th>

                <th>Overall</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="staffRow in staffRows" :key="staffRow.staffId">
                <td>
                  <strong>
                    {{ staffRow.staff.staff_name || "Unnamed staff member" }}
                  </strong>

                  <div class="grey-text staff-role">
                    {{ staffRow.staff.job_title || "No job title" }}
                  </div>
                </td>

                <td
                  v-for="courseStatus in staffRow.courseStatuses"
                  :key="courseStatus.course.id"
                >
                  <div
                    class="core-status-box"
                    :class="statusClass(courseStatus.status.state)"
                  >
                    <div class="core-status-box-title">
                      <i class="material-icons">
                        {{ courseStatus.status.icon }}
                      </i>

                      <strong>
                        {{ courseStatus.status.label }}
                      </strong>
                    </div>

                    <div class="core-status-box-meta">
                      {{ courseStatus.status.dateText }}
                    </div>
                  </div>
                </td>

                <td>
                  <strong
                    class="staff-percentage core-percent"
                    :class="percentageClass(staffRow.percentage)"
                  >
                    {{ staffRow.percentage }}%
                  </strong>

                  <div class="grey-text staff-current-count">
                    {{ staffRow.currentCount }}/{{ courses.length }} current
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { collection, getDocs } from "firebase/firestore";

import { db } from "@/firebase";

const COURSES = [
  {
    id: "allergen_awareness_uk_2026",
    shortTitle: "Allergens",
    renewalMonths: 4,
    graceDays: 7,
  },
  {
    id: "licensing_training",
    shortTitle: "Licensing",
    renewalMonths: 6,
    graceDays: 7,
  },
  {
    id: "fruit_machine_training",
    shortTitle: "Fruit Machines",
    renewalMonths: 6,
    graceDays: 7,
  },
];

function clean(value) {
  return String(value ?? "").trim();
}

function toDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === "function") {
    const date = value.toDate();

    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "object" && typeof value.seconds === "number") {
    const date = new Date(value.seconds * 1000);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function addMonths(value, months) {
  const source = toDate(value);

  if (!source) return null;

  const result = new Date(source);
  const originalDay = result.getDate();

  result.setMonth(result.getMonth() + Number(months || 0));

  if (result.getDate() !== originalDay) {
    result.setDate(0);
  }

  return result;
}

function addDays(value, days) {
  const source = toDate(value);

  if (!source) return null;

  const result = new Date(source);

  result.setDate(result.getDate() + Number(days || 0));

  return result;
}

function formatDate(value) {
  const date = toDate(value);

  if (!date) return "No date recorded";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default {
  name: "StaffTrainingProgressSummary",

  props: {
    venueId: {
      type: String,
      default: "",
    },

    staff: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,
      error: "",
      trainingRecords: [],
      courses: COURSES,
    };
  },

  computed: {
    cleanVenueId() {
      return clean(this.venueId);
    },

    activeStaff() {
      return (this.staff || [])
        .filter((staffMember) => {
          return staffMember && staffMember.active !== false;
        })
        .slice()
        .sort((a, b) => {
          const aName = clean(a?.staff_name).toLowerCase();

          const bName = clean(b?.staff_name).toLowerCase();

          return aName.localeCompare(bName);
        });
    },

    trainingRecordIndex() {
      const index = new Map();

      this.trainingRecords.forEach((record) => {
        const staffId = clean(
          record.staffId || record.staff_id || record.userId || record.user_id
        );

        const courseId = clean(record.courseId || record.course_id);

        if (!staffId || !courseId) return;

        const key = `${staffId}::${courseId}`;

        index.set(key, record);
      });

      return index;
    },

    staffRows() {
      return this.activeStaff.map((staffMember) => {
        const staffId = this.getStaffId(staffMember);

        const courseStatuses = this.courses.map((course) => {
          const key = `${staffId}::${course.id}`;

          const record = this.trainingRecordIndex.get(key) || null;

          return {
            course,
            record,
            status: this.buildTrainingStatus(record, course),
          };
        });

        const currentCount = courseStatuses.filter((item) => {
          return item.status.state === "current";
        }).length;

        const percentage =
          this.courses.length > 0
            ? Math.round((currentCount / this.courses.length) * 100)
            : 0;

        return {
          staffId,
          staff: staffMember,
          courseStatuses,
          currentCount,
          percentage,
        };
      });
    },

    allStatuses() {
      return this.staffRows.flatMap((staffRow) => {
        return staffRow.courseStatuses.map((item) => item.status);
      });
    },

    totalRequirements() {
      return this.activeStaff.length * this.courses.length;
    },

    currentCount() {
      return this.allStatuses.filter((status) => {
        return status.state === "current";
      }).length;
    },

    dueCount() {
      return this.allStatuses.filter((status) => {
        return status.state === "due";
      }).length;
    },

    actionCount() {
      return this.allStatuses.filter((status) => {
        return ["missing", "overdue"].includes(status.state);
      }).length;
    },

    overallPercentage() {
      if (!this.totalRequirements) {
        return 0;
      }

      return Math.round((this.currentCount / this.totalRequirements) * 100);
    },

    courseSummaries() {
      return this.courses.map((course) => {
        const statuses = this.staffRows.map((staffRow) => {
          return staffRow.courseStatuses.find((item) => {
            return item.course.id === course.id;
          })?.status;
        });

        const current = statuses.filter((status) => {
          return status?.state === "current";
        }).length;

        const due = statuses.filter((status) => {
          return status?.state === "due";
        }).length;

        const actionRequired = statuses.filter((status) => {
          return ["missing", "overdue"].includes(status?.state);
        }).length;

        const total = this.activeStaff.length;

        const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

        return {
          ...course,
          current,
          due,
          actionRequired,
          total,
          percentage,
        };
      });
    },
  },

  watch: {
    cleanVenueId: {
      immediate: true,

      handler() {
        this.loadTrainingRecords();
      },
    },
  },

  methods: {
    getStaffId(staffMember) {
      return clean(staffMember?.id || staffMember?.staff_id || staffMember?.staffId);
    },

    buildTrainingStatus(record, course) {
      const passed = record?.passed === true;

      const completedAt = toDate(
        record?.lastCompletedAt || record?.completedAt || record?.completed_at
      );

      if (!passed || !completedAt) {
        return {
          state: "missing",
          label: "Not completed",
          icon: "cancel",
          dateText: "No valid completion",
        };
      }

      const nextDueAt =
        toDate(record?.nextDueAt || record?.next_due_at) ||
        addMonths(completedAt, course.renewalMonths);

      const graceEndsAt =
        toDate(record?.graceEndsAt || record?.grace_ends_at) ||
        addDays(nextDueAt, course.graceDays);

      const now = new Date();

      if (nextDueAt && now < nextDueAt) {
        return {
          state: "current",
          label: "Current",
          icon: "check_circle",
          dateText: `Due ${formatDate(nextDueAt)}`,
        };
      }

      if (nextDueAt && graceEndsAt && now >= nextDueAt && now <= graceEndsAt) {
        return {
          state: "due",
          label: "Due",
          icon: "schedule",
          dateText: `Complete by ${formatDate(graceEndsAt)}`,
        };
      }

      return {
        state: "overdue",
        label: "Overdue",
        icon: "error",
        dateText: graceEndsAt ? `Expired ${formatDate(graceEndsAt)}` : "Training expired",
      };
    },

    percentageClass(percentage) {
      const value = Number(percentage || 0);

      if (value >= 100) {
        return "core-percent--success";
      }

      if (value >= 67) {
        return "core-percent--warning";
      }

      return "core-percent--danger";
    },

    statusClass(state) {
      return {
        "core-status-box--success": state === "current",

        "core-status-box--warning": state === "due",

        "core-status-box--danger": state === "missing" || state === "overdue",
      };
    },

    async loadTrainingRecords() {
      this.error = "";
      this.trainingRecords = [];

      if (!this.cleanVenueId) {
        this.loading = false;
        return;
      }

      this.loading = true;

      try {
        const recordsRef = collection(
          db,
          "venues",
          this.cleanVenueId,
          "training_records"
        );

        const snapshot = await getDocs(recordsRef);

        this.trainingRecords = snapshot.docs.map((document) => {
          return {
            id: document.id,
            ...document.data(),
          };
        });
      } catch (error) {
        console.error(
          "[StaffTrainingProgressSummary] failed to load training records:",
          error
        );

        this.error = error?.message || "Failed to load training records.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.training-summary-card {
  margin-top: 16px;
}

.overall-summary {
  display: flex;
  align-items: center;
  gap: 16px;
}

.overall-percentage {
  min-width: 95px;
  font-size: 32px;
}

.overall-details {
  display: flex;
  flex-direction: column;
}

.overall-details strong {
  font-size: 17px;
}

.overall-details span {
  margin-top: 4px;
  color: #757575;
  font-size: 12px;
}

.overall-progress-track {
  margin-top: 14px;
}

.summary-kpis {
  margin-top: 15px;
}

.summary-kpi strong {
  font-size: 22px;
}

.course-summary-grid {
  margin-top: 16px;
}

.course-summary-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.course-summary-heading > div {
  display: flex;
  flex-direction: column;
}

.course-summary-heading span {
  margin-top: 3px;
  color: #757575;
  font-size: 11px;
}

.course-progress-track {
  margin-top: 12px;
}

.course-status-counts {
  display: flex;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
  font-size: 11px;
}

.training-table-scroll {
  margin-top: 18px;
}

.training-table {
  min-width: 950px;
}

.staff-role,
.staff-current-count {
  margin-top: 3px;
  font-size: 11px;
}

.staff-percentage {
  font-size: 18px;
}

@media (max-width: 900px) {
  .course-summary-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .overall-summary {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
